/**
 * Type-Safe MLB Sabotage Catalog
 * 
 * Every stat_column reference is validated against VALID_STAT_COLUMNS,
 * so typos are caught at compile time rather than silently failing at scoring.
 * 
 * Rarity tiers:
 *   common    — 1/week per team, targets one opponent (display only)
 *   rare      — unlimited per week, targets one opponent (display only)
 *   legendary — 1/season per team, GLOBAL (affects ALL teams' matchups/draft)
 * 
 * Sabotage effects can be BILATERAL (both teams), TARGETED (opponent only),
 * or SELF-BUFF (your team only). The effect_config.target field determines targeting.
 */
import type { SabotageCatalogEntry } from '@/types/sabotageStats';

export const MLB_MATCHUP_SABOTAGES: SabotageCatalogEntry[] = [
  // ─── COMMON ──────────────────────────────────────────
  {
    name: 'Balk Call',
    icon: '⚾',
    description: "All pitchers in this matchup have their walks, balks, and hit batters count double for the rest of this matchup week. Play it when your pitchers are wilder than your opponent's.",
    effect_type: 'stat_multiplier',
    effect_config: { stat_column: 'walks_allowed', multiplier: 2, extra_columns: ['balks', 'hit_batsmen'] },
    color: 'damage',
    context: 'matchup',
    rarity: 'common',
  },
  {
    name: 'Butterfingers',
    icon: '🧤',
    description: "All errors committed by both teams in this matchup count for 1.5x points for the rest of this matchup week. Play it when your fielders have more butterfingers than your opponent's.",
    effect_type: 'stat_multiplier',
    effect_config: { stat_column: 'errors', multiplier: 1.5 },
    color: 'damage',
    context: 'matchup',
    rarity: 'common',
  },
  {
    name: "Fielder's Choice",
    icon: '🏃',
    description: "Your team's stolen base penalties are neutralized for the rest of this matchup week. Only affects your roster.",
    effect_type: 'self_stat_nullifier',
    effect_config: { stat_column: 'stolen_bases', target: 'self_hitters' },
    color: 'success',
    context: 'matchup',
    rarity: 'common',
  },

  // ─── RARE ────────────────────────────────────────────
  {
    name: 'Pitching Change',
    icon: '🔄',
    description: "At the end of this matchup week, your opponent's highest-scoring pitcher's score is replaced with their lowest-scoring pitcher's score. Only affects your opponent.",
    effect_type: 'pitcher_swap',
    effect_config: { target: 'opponent' },
    color: 'damage',
    context: 'matchup',
    rarity: 'rare',
  },
  {
    name: 'K-Pocalypse',
    icon: '💀',
    description: "All batter strikeouts for both teams in this matchup count for double points for the rest of this matchup week. Gamble on who strikes out more.",
    effect_type: 'stat_multiplier',
    effect_config: { stat_column: 'strikeouts', multiplier: 2 },
    color: 'damage',
    context: 'matchup',
    rarity: 'rare',
  },
  {
    name: 'Gopher Ball',
    icon: '🏠',
    description: "All home runs allowed by both teams' pitchers in this matchup count for double points for the rest of this matchup week. Play it when your staff gives up more bombs.",
    effect_type: 'stat_multiplier',
    effect_config: { stat_column: 'home_runs_allowed', multiplier: 2 },
    color: 'damage',
    context: 'matchup',
    rarity: 'rare',
  },
  {
    name: 'Juiced Balls',
    icon: '💉',
    description: "Your hitters' penalties from home runs, RBIs, hits, doubles, and triples are reduced by 50% for the rest of this matchup week. Only affects your roster.",
    effect_type: 'self_weight_modifier',
    effect_config: { stat_columns: ['home_runs', 'rbi', 'hits', 'doubles', 'triples'], reduction_pct: 50, target: 'self_hitters' },
    color: 'success',
    context: 'matchup',
    rarity: 'rare',
  },

  // ─── LEGENDARY ───────────────────────────────────────
  {
    name: 'Seventh Inning Curse',
    icon: '7️⃣',
    description: "All pitchers across EVERY matchup in the league have their earned runs count double for the rest of this matchup week. League-wide chaos.",
    effect_type: 'stat_multiplier',
    effect_config: { stat_column: 'earned_runs', multiplier: 2 },
    color: 'damage',
    context: 'matchup',
    rarity: 'legendary',
  },
  {
    name: 'Error Jackpot',
    icon: '🎰',
    description: "All errors across EVERY matchup in the league count for triple points for the rest of this matchup week. League-wide chaos.",
    effect_type: 'stat_multiplier',
    effect_config: { stat_column: 'errors', multiplier: 3 },
    color: 'damage',
    context: 'matchup',
    rarity: 'legendary',
  },
  {
    name: 'Closer Curse',
    icon: '🚪',
    description: "All blown saves across EVERY matchup in the league count for triple points for the rest of this matchup week. League-wide chaos.",
    effect_type: 'stat_multiplier',
    effect_config: { stat_column: 'blown_saves', multiplier: 3 },
    color: 'damage',
    context: 'matchup',
    rarity: 'legendary',
  },
];

// Draft-time sabotages
export const MLB_DRAFT_SABOTAGES: SabotageCatalogEntry[] = [
  {
    name: 'Half Time',
    icon: '⏱️',
    description: "Cut opponent's draft timer in half for their next pick. Minimum 15 seconds.",
    effect_type: 'timer_reduction',
    effect_config: { reduction_factor: 0.5, min_seconds: 15 },
    color: 'damage',
    context: 'draft',
    rarity: 'common',
  },
  {
    name: 'Injury Report',
    icon: '🩹',
    description: "Show a random top player as injured (red tag) to all participants for one pick. It's a bluff!",
    effect_type: 'fake_injury',
    effect_config: { top_n: 50 },
    color: 'damage',
    context: 'draft',
    rarity: 'rare',
  },
  {
    name: 'Snake Reversal',
    icon: '🐍',
    description: "Reverse the draft order for the next complete round. Chaos in the snake!",
    effect_type: 'order_reversal',
    effect_config: {},
    color: 'damage',
    context: 'draft',
    rarity: 'legendary',
  },
  {
    name: 'Pick Skip',
    icon: '⏭️',
    description: "Opponent loses their next pick (round 10+). If used earlier, it auto-applies at round 10.",
    effect_type: 'pick_skip',
    effect_config: { min_round: 10 },
    color: 'damage',
    context: 'draft',
    rarity: 'rare',
  },
  {
    name: 'Guess Who',
    icon: '🎭',
    description: "Hide player names and photos from your opponent for their next 3 picks. They draft blind!",
    effect_type: 'guess_who',
    effect_config: { duration_picks: 3 },
    color: 'damage',
    context: 'draft',
    rarity: 'common',
  },
  {
    name: 'Karma',
    icon: '🔄',
    description: "Reverse the last sabotage used against you back onto the attacker. Requires 2+ targeted sabotages against you.",
    effect_type: 'karma',
    effect_config: { hit_count_threshold: 2 },
    color: 'success',
    context: 'draft',
    rarity: 'rare',
  },
];